/* tslint:disable:max-line-length */
import { Injectable } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { HttpClient } from '@angular/common/http';
import { PtLuxonDate } from '../../models/projects/date.models';
import {
  JackingActivityEditor,
  JackingActivityEventCollectionType,
  JackingActivityEventEditor,
  JackingActivityGuids,
  JackingActivityStatuses,
  JackingActivityTypes, UpdatedJackingDataModel,
} from '../../models/projects/jacking-activity.models';
import { DailyLogOperationInstance } from '../../models/projects/operation.models';
import { DateParserService } from '../../services/daily-log/daily-log-time/date-parser-service';
import { DateService } from '../../services/date/date.service';
import { TimeZoneService } from '../../services/time-zone/time-zone.service';
import { VesselService } from '../../services/vessel/vessel.service';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { cloneDeep } from 'lodash';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';

@Injectable()
export class JackingActivityService {
  config = AppConfig.settings;
  private readonly DAILY_LOG_KEY: string = 'daily_log_key';
  private state: State = {
    take: 50,
    skip: 0
  };

  private jackingActivitiesTable: DbTable;
  private readonly JackingArrivalPortOperationGuid =
    'A5EBDE53-653D-425B-B33D-D179CD0D5C91';
  private readonly JackingArrivalOffshoreOperationGuid =
    'DE88783E-5898-402B-A977-52516B07D925';

  private readonly JackDownPortOperationGuid =
    'BFB2438A-26F7-4B9E-B470-19C4E253170B';
  private readonly JackDownOffshoreOperationGuid =
    '44D90FAB-3DEA-4850-A9CB-B9A439A5DAB5';

  // the below guids now belong to archived operations
  // need to keep them so that old jacking ops in implementer mode still work
  private readonly JackingArrivalOperationGuid =
    '9DAE9105-9E27-4305-BE6D-D9F111B8D45D';
  private readonly JackUpPortOperationGuid =
    '05F0E3F3-390B-4D18-873C-76EFDB1AEA1A';
  private readonly JackUpOffshoreOperationGuid =
    '69E83BA3-F382-47C7-A3A5-52CBAF3D449B';

  constructor(
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    public vesselService: VesselService,
    private timeZoneService: TimeZoneService,
    private http: HttpClient,
  ) {
    this.jackingActivitiesTable = this.dbContext.tables.find(
      t => t.name === 'JackingActivities'
    );
  }

  getState(): State {
    return this.state;
  }

  setState(state: State) {
    this.state = state;
  }

  getCurrentLogDay(): Date {
    const json = localStorage.getItem(this.DAILY_LOG_KEY);
    const date = JSON.parse(json);

    if (!date) {
      return new Date();
    } else {
      return new Date(date);
    }
  }

  setCurrentLogDay(date: DateTime) {
    localStorage.setItem(this.DAILY_LOG_KEY, JSON.stringify(date));
  }

  /// Get's activities where the end date is the same as the date passed
  public async getActivitiesCompleteForDay(
    date?: PtLuxonDate
  ): Promise<JackingActivityEditor[]> {
    let activities = (await this.dbContext.getAll(this.jackingActivitiesTable));

    for (const activity of activities) {
      // used to call deserializeJackingActivityDates, but we only need endDate to check for day, and confirmed here, so removing it to speed up validation
      // await this.deserializeJackingActivityDates(activity, false);

      // End Date
      activity.endDate = await this.dateParserService.deserializePtLuxonDate(activity.endDate, this.timeZoneService);
      activity.endDay = await activity.endDate.getDay();
    }

    const currentDay = await date.getDay();
    activities = activities.filter(a => currentDay === a.endDay);

    return activities;
  }

  /// Get's activities where the date passed is between the start/end date of the activity
  public async getActivityDataForDay(date: PtLuxonDate, endOfDay: PtLuxonDate): Promise<JackingActivityEditor[]> {
    let activities = (await this.dbContext.getAll(this.jackingActivitiesTable)) as JackingActivityEditor[];

    activities = await this.dateParserService.deserializeDate(activities, true, this.timeZoneService);

    for (const activity of activities) {
      await this.deserializeJackingActivityDates(activity, true, false);
    }

    // filter for the following:
    // where activity start date happens on the current date
    // where activity end date falls on the current date
    // where the current date falls between the start and end day, i.e an activity that spans 3+ days
    // where activity starts on previous day and has no end date
    activities = activities.filter(a => a.startDate.dateTime >= date.dateTime && a.startDate.dateTime < endOfDay.dateTime
      || (a.endDate.dateTime >= date.dateTime && a.endDate.dateTime < endOfDay.dateTime)
      || (date.dateTime >= a.startDate.dateTime && date.dateTime <= a.endDate.dateTime)
      || (a.startDate.dateTime <= date.dateTime && a.endDate.dateTime === null));

    return activities;
  }

  private async deserializeJackingActivityDates(
    activity: JackingActivityEditor,
    removeSystemTime: boolean = false,
    partialLoad: boolean = false // pass optional timezones in - much faster than hitting local storage continuously to get the same exact list
  ) {
    // Start Date
    activity.startDate = await this.dateParserService.deserializePtLuxonDate(activity.startDate, this.timeZoneService);
    activity.startDay = activity.startDate.getDay();
    // showStartDate set below after system offset is removed

    // End Date
    activity.endDate = await this.dateParserService.deserializePtLuxonDate(activity.endDate, this.timeZoneService);
    activity.endDay = await activity.endDate.getDay();
    // showEndDate set below after system offset is removed

    // only need to load the above on certain pages - below is needed when actually editing the jacking pages
    // partialLoad will be true when called from operationAdd/Edit pages
    if (!partialLoad) {
      // Last Modified By
      activity.lastModifiedDate = await this.dateParserService.deserializePtLuxonDate(activity.lastModifiedDate, this.timeZoneService);
      activity.showLastModifiedDate = await this.dateService.setFullTimeString(activity.lastModifiedDate.getDate());

      // Max Pulling Time
      activity.legInfo.leg1.legMaxPullingloadTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg1.legMaxPullingloadTime, this.timeZoneService);
      activity.legInfo.leg2.legMaxPullingloadTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg2.legMaxPullingloadTime, this.timeZoneService);
      activity.legInfo.leg3.legMaxPullingloadTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg3.legMaxPullingloadTime, this.timeZoneService);
      activity.legInfo.leg4.legMaxPullingloadTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg4.legMaxPullingloadTime, this.timeZoneService);

      // Leg free in holes Time
      activity.legInfo.leg1.legFreeInHolesExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg1.legFreeInHolesExtTime, this.timeZoneService);
      activity.legInfo.leg2.legFreeInHolesExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg2.legFreeInHolesExtTime, this.timeZoneService);
      activity.legInfo.leg3.legFreeInHolesExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg3.legFreeInHolesExtTime, this.timeZoneService);
      activity.legInfo.leg4.legFreeInHolesExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg4.legFreeInHolesExtTime, this.timeZoneService);

      // Clear Of Seabed Time
      activity.legInfo.leg1.clearOfSeaBedExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg1.clearOfSeaBedExtTime, this.timeZoneService);
      activity.legInfo.leg2.clearOfSeaBedExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg2.clearOfSeaBedExtTime, this.timeZoneService);
      activity.legInfo.leg3.clearOfSeaBedExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg3.clearOfSeaBedExtTime, this.timeZoneService);
      activity.legInfo.leg4.clearOfSeaBedExtTime = await this.dateParserService.deserializePtLuxonDate(activity.legInfo.leg4.clearOfSeaBedExtTime, this.timeZoneService);

      // Final Leg values time
      activity.finalJackingInfo.finalLegValuesTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.finalLegValuesTime, this.timeZoneService);

      // Final Difference From Expected time
      activity.finalJackingInfo.differenceFromExpectedTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.differenceFromExpectedTime, this.timeZoneService);

      // Final Penetrations time
      activity.finalJackingInfo.finalPenetrationsTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.finalPenetrationsTime, this.timeZoneService);

      // DeepWell Tie In time
      activity.finalJackingInfo.deepWellTieInDepthTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.deepWellTieInDepthTime, this.timeZoneService);

      // DeepWell Tie In Number
      activity.finalJackingInfo.deepWellTieInNumberTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.deepWellTieInNumberTime, this.timeZoneService);

      // Davit Pump Used time
      activity.finalJackingInfo.davitPumpUsedTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.davitPumpUsedTime, this.timeZoneService);

      // Seajets Used time
      activity.finalJackingInfo.seajetsUsedTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.seajetsUsedTime, this.timeZoneService);

      // Final Air Gap time
      activity.finalJackingInfo.finalAirGapTime = await this.dateParserService.deserializePtLuxonDate(activity.finalJackingInfo.finalAirGapTime, this.timeZoneService);

      // Event Dates
      for (const evnt of activity.events) {
        evnt.time = await this.dateParserService.deserializePtLuxonDate(evnt.time, this.timeZoneService);
      }
    }

    if (removeSystemTime) {
      // local dates get set here too
      // todo - remove if not used or needed now all local dates set above
      // await this.removeSystemOffset(activity, partialLoad);

      activity.showStartDate = await this.dateService.setFullTimeString(activity.startDate.dateTime);
      activity.showEndDate = await this.dateService.setFullTimeString(activity.endDate.dateTime);
    } else {
      // if !removeSystemTime, then localDates will be null, so we need to set this via the .date
      activity.showStartDate = await this.dateService.setFullTimeString(activity.startDate.getDate());
      activity.showEndDate = await this.dateService.setFullTimeString(activity.endDate.getDate());
    }

    // Offsets must be calculated after removing the system offset
    // Time strings must be calculated after removing the system offset
    // todo - this doesn't seem necessary any more, cant we just take the offset from the startTime?
    activity.startOffset = await this.timeZoneService.offsetForTime(activity.startDate.dateTime);

    activity.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.endDate);
    activity.endOffset = await this.timeZoneService.offsetForTime(activity.endDate.dateTime);

    // only need to load the above on certain pages - below is needed when actually editing the jacking pages

    // todo - replace .offsetForTime() with just assigning offset from the ptLuxonDate
    if (!partialLoad) {
      // Max Pulling Time - Leg 1
      activity.legInfo.leg1.legMaxPullingloadTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg1.legMaxPullingloadTime);
      activity.legInfo.leg1.legMaxPullingLoadTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg1.legMaxPullingloadTime.getDate());

      // Max Pulling Time - Leg 2
      activity.legInfo.leg2.legMaxPullingloadTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg2.legMaxPullingloadTime);
      activity.legInfo.leg2.legMaxPullingLoadTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg2.legMaxPullingloadTime.getDate());

      // Max Pulling Time - Leg 3
      activity.legInfo.leg3.legMaxPullingloadTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg3.legMaxPullingloadTime);
      activity.legInfo.leg3.legMaxPullingLoadTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg3.legMaxPullingloadTime.getDate());

      // Max Pulling Time - Leg 4
      activity.legInfo.leg4.legMaxPullingloadTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg4.legMaxPullingloadTime);
      activity.legInfo.leg4.legMaxPullingLoadTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg4.legMaxPullingloadTime.getDate());

      // Free In Holes Time - Leg 1
      activity.legInfo.leg1.legFreeInHolesExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg1.legFreeInHolesExtTime);
      activity.legInfo.leg1.legFreeInHolesExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg1.legFreeInHolesExtTime.getDate());

      // Free In Holes Time - Leg 2
      activity.legInfo.leg2.legFreeInHolesExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg2.legFreeInHolesExtTime);
      activity.legInfo.leg2.legFreeInHolesExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg2.legFreeInHolesExtTime.getDate());

      // Free In Holes Time - Leg 3
      activity.legInfo.leg3.legFreeInHolesExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg3.legFreeInHolesExtTime);
      activity.legInfo.leg3.legFreeInHolesExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg3.legFreeInHolesExtTime.getDate());

      // Free In Holes Time - Leg 4
      activity.legInfo.leg4.legFreeInHolesExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg4.legFreeInHolesExtTime);
      activity.legInfo.leg4.legFreeInHolesExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg4.legFreeInHolesExtTime.getDate());

      // Clear Of Seabed Time - Leg 1
      activity.legInfo.leg1.clearOfSeaBedExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg1.clearOfSeaBedExtTime);
      activity.legInfo.leg1.clearOfSeabedExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg1.clearOfSeaBedExtTime.getDate());

      // Clear Of Seabed Time - Leg 2
      activity.legInfo.leg2.clearOfSeaBedExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg2.clearOfSeaBedExtTime);
      activity.legInfo.leg2.clearOfSeabedExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg2.clearOfSeaBedExtTime.getDate());

      // Clear Of Seabed Time - Leg 3
      activity.legInfo.leg3.clearOfSeaBedExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg3.clearOfSeaBedExtTime);
      activity.legInfo.leg3.clearOfSeabedExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg3.clearOfSeaBedExtTime.getDate());

      // Clear Of Seabed Time - Leg 4
      activity.legInfo.leg4.clearOfSeaBedExtTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.legInfo.leg4.clearOfSeaBedExtTime);
      activity.legInfo.leg4.clearOfSeabedExtTimeOffset = await this.timeZoneService.offsetForTime(activity.legInfo.leg4.clearOfSeaBedExtTime.dateTime);

      // Final Leg values time
      activity.finalJackingInfo.finalLegValuesTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.finalLegValuesTime);
      activity.finalJackingInfo.finalLegValuesTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.finalLegValuesTime.dateTime);

      // Final Difference From Expected time
      activity.finalJackingInfo.differenceFromExpectedTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.differenceFromExpectedTime);
      activity.finalJackingInfo.differenceFromExpectedTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.differenceFromExpectedTime.dateTime);

      // Final Penetrations time
      activity.finalJackingInfo.finalPenetrationsTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.finalPenetrationsTime);
      activity.finalJackingInfo.finalPenetrationsTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.finalPenetrationsTime.dateTime);

      // DeepWell Tie In time
      activity.finalJackingInfo.deepWellTieInDepthTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.deepWellTieInDepthTime);
      activity.finalJackingInfo.deepWellTieInDepthTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.deepWellTieInDepthTime.dateTime);

      // DeepWell Tie In Number
      activity.finalJackingInfo.deepWellTieInNumberTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.deepWellTieInNumberTime);
      activity.finalJackingInfo.deepWellTieInNumberTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.deepWellTieInNumberTime.dateTime);

      // Davit Pump Used time
      activity.finalJackingInfo.davitPumpUsedTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.davitPumpUsedTime);
      activity.finalJackingInfo.davitPumpUsedTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.davitPumpUsedTime.dateTime);

      // Seajets Used time
      activity.finalJackingInfo.seajetsUsedTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.seajetsUsedTime);
      activity.finalJackingInfo.seajetsUsedTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.seajetsUsedTime.dateTime);

      // Final Air Gap time
      activity.finalJackingInfo.finalAirGapTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(activity.finalJackingInfo.finalAirGapTime);
      activity.finalJackingInfo.finalAirGapTimeOffset = await this.timeZoneService.offsetForTime(activity.finalJackingInfo.finalAirGapTime.dateTime);

      for (const evnt of activity.events) {
        evnt.timeOffset = await this.timeZoneService.offsetForTime(evnt.time.dateTime);
        evnt.timeToShow = this.dateService.setTimeStringFromPtLuxonDate(evnt.time);
      }
    }

    activity.activityTypeName = activity.type.name;
    activity.activityStatusName = activity.status.name;
  }

  public getJackingActivityGuids(): JackingActivityGuids {
    return {
      jackDownOffshoreOperationGuid: this.JackDownOffshoreOperationGuid,
      jackDownPortOperationGuid: this.JackDownPortOperationGuid,
      jackUpOffshoreOperationGuid: this.JackUpOffshoreOperationGuid,
      jackUpPortOperationGuid: this.JackUpPortOperationGuid,
      jackingArrivalOperationGuid: this.JackingArrivalOperationGuid,
      jackingArrivalPortOperationGuid: this.JackingArrivalPortOperationGuid,
      jackingArrivalOffshoreOperationGuid: this.JackingArrivalOffshoreOperationGuid,
    };
  }

  // TODO-RYAN: Add SubClassificationID to operation summary
  // Should this be elsewhere?
  public operationRequiresJackingActivity(
    operationInstance: DailyLogOperationInstance
  ) {
    return (
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackUpOffshoreOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackDownOffshoreOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackUpPortOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackDownPortOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackingArrivalOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackingArrivalPortOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackingArrivalOffshoreOperationGuid.toLowerCase()
    );
  }

  public async deleteAllLocalJackingActivities() {
    await this.dbContext.removeAll(this.jackingActivitiesTable);
  }

  public saveJackingActivityLocally(activity: JackingActivityEditor) {
    this.dbContext.addOrUpdate(
      this.jackingActivitiesTable,
      activity.id,
      activity
    );
  }

  public async saveJackingActivitiesLocally(activies: JackingActivityEditor[]) {
    for (let i = 0; i < activies.length; i++) {
      this.saveJackingActivityLocally(activies[i]);
    }
  }

  // Creates a new Jacking Activity linked to this operation if one isn't assigned.
  // Updates the currently linked activity & events
  // Returns the Jacking Activity in the promise
  public async createOrUpdateActivityForOperation(
    operationInstance: DailyLogOperationInstance,
    lastLocationId: number = null,
    currentLocationId: number = null,
    nextLocationId: number = null
  ): Promise<JackingActivityEditor> {
    // Check for null operation
    if (operationInstance === undefined || operationInstance === null) {
      throw new Error('OperationInstance must not be null');
    }

    // Check if operation is of the right type
    if (!this.operationRequiresJackingActivity(operationInstance)) {
      throw new Error(
        'Only operations of subclassifcation "Jacking" require Jacking Activities'
      );
    }

    let jackingActivity: JackingActivityEditor;
    if (!operationInstance.jackingActivityId) {
      jackingActivity = await this.CreateJackingActivityForOperation(
        operationInstance
      );
    } else {
      jackingActivity = await this.dbContext
        .getAll(this.jackingActivitiesTable)
        .then(activities => {
          const activity = activities.find(
            a =>
              a.id === operationInstance.jackingActivityId ||
              a.localId === operationInstance.jackingActivityId
          );
          return activity;
        });
      if (jackingActivity) {
        await this.deserializeJackingActivityDates(jackingActivity, null, true); // work around as PtLuxonDate objects need to be reassigned
      }
    }

    if (jackingActivity) {
      operationInstance.jackingActivityId = jackingActivity.localId;
      this.updateActivityForOperation(jackingActivity, operationInstance);
      if (jackingActivity.type.id === JackingActivityTypes.Arrival) {
        jackingActivity.projectInfo.lastLocationId = lastLocationId;
        jackingActivity.projectInfo.currentLocationId = nextLocationId;
      } else {
        jackingActivity.projectInfo.currentLocationId = currentLocationId;
        jackingActivity.projectInfo.nextLocationId = nextLocationId;
      }
      return jackingActivity;
    }
  }

  private updateActivityForOperation(
    jackingActivity: JackingActivityEditor,
    operationInstance: DailyLogOperationInstance
  ) {
    if (jackingActivity) {
      // because arrival activity is made from 2 operations, only set the start/end when editing the correct operation
      // departure only links to 1 operation so set them both
      switch (jackingActivity.type.id) {
        case 'arrival': {
          if (operationInstance.id && operationInstance.id == jackingActivity.primaryOperationId) {
            jackingActivity.startDate = operationInstance.startTime;
            jackingActivity.startOffset = operationInstance.startTimeOffset;

            // if secondaryOpId is null, then user hasn't added the 'jack up - op' yet, so set the end time too
            if (jackingActivity.secondaryOperationId === null) {
              jackingActivity.endDate = operationInstance.endTime;
              jackingActivity.endOffset = operationInstance.endTimeOffset;
            }
          } else {
            jackingActivity.endDate = operationInstance.endTime;
            jackingActivity.endOffset = operationInstance.endTimeOffset;
          }

          break;
        }
        case 'departure': {
          jackingActivity.startDate = operationInstance.startTime;
          jackingActivity.startOffset = operationInstance.startTimeOffset;
          jackingActivity.endDate = operationInstance.endTime;
          jackingActivity.endOffset = operationInstance.endTimeOffset;

          break;
        }
      }
    }
  }

  private getJackingActivityLocalId(): Promise<number> {
    return this.dbContext.getTempId(this.jackingActivitiesTable).then(id => {
      return <number>id;
    });
  }

  // Jacking Activity Constructor Methods
  // Todo-Ryan probably better to turn these into an abstract factory pattern
  private async CreateJackingActivityForOperation(
    operationInstance: DailyLogOperationInstance
  ): Promise<JackingActivityEditor> {
    // Check if this operation already has an assigned activity
    if (operationInstance.jackingActivityId) {
      throw new Error('Operation already has an assigned activity');
    }

    // Call constructor methods based on Operation Type (Arrival, Jack Up, Jack Down, On-Site Movement)
    if (
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackingArrivalOperationGuid.toLowerCase()) {
      // obsolete but kept for compatibility
      return this.buildObsoleteJackingArrivalActivity(operationInstance);
    } else if (
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackDownPortOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackDownOffshoreOperationGuid.toLowerCase()
    ) {
      // obsolete but kept for compatibility
      return this.buildJackDownActivity(operationInstance);
    } else if (
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackUpPortOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackUpOffshoreOperationGuid.toLowerCase()
    ) {
      // obsolete but kept for compatibility
      return this.buildObsoleteJackUpActivity(operationInstance);
    } else if (operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackingArrivalPortOperationGuid.toLowerCase() ||
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackingArrivalOffshoreOperationGuid.toLowerCase()
    ) {
      return this.buildJackingArrivalActivity(operationInstance);
    } else {
      throw new Error(
        'Unknown operation instance type to construct Jacking Activity'
      );
    }
  }

  public isArrivalJackingOperation(operationInstance: DailyLogOperationInstance): boolean {
    const opGuid =
      operationInstance?.projectSpecificOperation?.operationSummary?.guid?.toLowerCase();

    if (!opGuid) {
      throw new Error('Operation instance is missing operationSummary.guid');
    }

    // Arrival (both new + legacy)
    const arrivalGuids = new Set<string>([
      this.JackingArrivalOperationGuid.toLowerCase(),        // legacy arrival
      this.JackingArrivalPortOperationGuid.toLowerCase(),    // v1/v2 arrival port
      this.JackingArrivalOffshoreOperationGuid.toLowerCase() // v1/v2 arrival offshore
    ]);

    if (arrivalGuids.has(opGuid)) {
      return true;
    }

    // Not-arrival (known non-arrival types)
    const knownNonArrivalGuids = new Set<string>([
      this.JackDownPortOperationGuid.toLowerCase(),
      this.JackDownOffshoreOperationGuid.toLowerCase(),
      this.JackUpPortOperationGuid.toLowerCase(),
      this.JackUpOffshoreOperationGuid.toLowerCase()
    ]);

    if (knownNonArrivalGuids.has(opGuid)) {
      return false;
    }

    throw new Error('Unknown operation instance type to determine arrival/departure');
  }

  private async buildJackingActivityGeneric(
    operationInstance: DailyLogOperationInstance
  ): Promise<JackingActivityEditor> {
    const activity = new JackingActivityEditor();
    activity.localId = await this.getJackingActivityLocalId();
    activity.localPrimaryOperationId = operationInstance.localId;
    activity.primaryOperationId = operationInstance.id;
    activity.startDate = operationInstance.startTime;
    activity.endDate = operationInstance.endTime;
    activity.startOffset = operationInstance.startTime.dateTime.offset / 60;
    activity.endOffset = operationInstance.endTime && operationInstance.endTime.dateTime ? operationInstance.endTime.dateTime.offset / 60 : 0;
    activity.guid = this.getGuid();

    activity.status.id = JackingActivityStatuses.NotStarted;
    activity.status.name = 'Not Started';

    // Project Info Defaults
    const vessel = await this.vesselService.getEditor();
    activity.projectInfo.vesselName = vessel.name;
    activity.projectInfo.vesselClassId = vessel.vesselClassId;

    return activity;
  }

  private async buildJackingArrivalActivity(operationInstance: DailyLogOperationInstance)
    : Promise<JackingActivityEditor> {

    const activity = await this.buildJackingActivityGeneric(operationInstance);

    activity.type.id = JackingActivityTypes.Arrival;
    activity.type.name = 'Arrival';

    await this.deserializeJackingActivityDates(activity, false, true); // work around as PtLuxonDate objects need to be reassigned

    this.createHardCodedJackingEventsForJackingArrival(activity);

    // set the secondary operation id as the same id, a bit hacky - jacking arrival used to be made up of 2 operations
    // the primary op (jacking arrival) and secondary op (jack up port/offshore)
    // these have been combined into one, but we still need the previously added operations to function, so still need
    // to have the two primary and secondary ids as part of the db schema, so just set secondary=primary as workaround
    activity.localSecondaryOperationId = operationInstance.localId;
    activity.secondaryOperationId = operationInstance.id;

    this.createOperationStepEventsForJackingArrival(activity, operationInstance);

    return activity;
  }

  // replaced by the combined jacking arrival + jack up, but kept for old ops
  private async buildObsoleteJackingArrivalActivity(
    operationInstance: DailyLogOperationInstance
  ): Promise<JackingActivityEditor> {
    const activity = await this.buildJackingActivityGeneric(operationInstance);

    activity.type.id = JackingActivityTypes.Arrival;
    activity.type.name = 'Arrival';

    // Create events for each operation step instance
    // These will be suplimented by hard-coded events for each operation type
    for (
      let i = 0;
      i < operationInstance.stepCollectionInstance.stepInstances.length;
      i++
    ) {
      const step = operationInstance.stepCollectionInstance.stepInstances[i];
      const alreadyAssignedLinkedStep = operationInstance.stepCollectionInstance.stepInstances.find(
        s => s.group === step.group && (s.guid !== null || s.guid !== undefined)
      );
      let event: JackingActivityEventEditor = null;
      if (alreadyAssignedLinkedStep.step.jackingEventCollectionType) {
        if (alreadyAssignedLinkedStep.jackingEventGuid) {
          event = activity.events.find(
            e => e.guid === alreadyAssignedLinkedStep.jackingEventGuid
          );
        }
        if (!event) {
          event = new JackingActivityEventEditor();
          event.guid = this.getGuid();
          event.description = step.step.name;
          event.jackingEventCollectionType =
            step.step.jackingEventCollectionType;
          event.order = this.getJackingArrivalEventOrderForStep(
            event,
            activity.projectInfo.vesselClassId
          );
          event.isLinkedToStep = true;
          step.jackingEventGuid = event.guid;
          activity.events.push(event);
        }
      }
    }

    // Add hardcoded events per vessel class
    if (activity.projectInfo.vesselClassId === 1) {
      // NG5500X
      const startEvent = new JackingActivityEventEditor();
      startEvent.order = 0;
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start Position';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(startEvent);

      const tagEvent = new JackingActivityEventEditor();
      tagEvent.order = 1;
      tagEvent.guid = this.getGuid();
      tagEvent.description = 'Tagging';
      tagEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(tagEvent);

      const pinEvent = new JackingActivityEventEditor();
      pinEvent.order = 2;
      pinEvent.guid = this.getGuid();
      pinEvent.description = 'Pinned';
      pinEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(pinEvent);

      const penetrationsAfterActivePreloadEvent = new JackingActivityEventEditor();
      penetrationsAfterActivePreloadEvent.order = 6;
      penetrationsAfterActivePreloadEvent.guid = this.getGuid();
      penetrationsAfterActivePreloadEvent.description =
        'Penetrations after active preload';
      penetrationsAfterActivePreloadEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsAfterActivePreloadEvent);

      const airGapEvent = new JackingActivityEventEditor();
      airGapEvent.guid = this.getGuid();
      airGapEvent.order = 9;
      airGapEvent.description = 'Air gap';
      airGapEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.AirGap;
      activity.events.push(airGapEvent);
    } else if (activity.projectInfo.vesselClassId === 2) {
      // NG2500X
      const startEvent = new JackingActivityEventEditor();
      startEvent.order = 0;
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start Position';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(startEvent);

      const tagEvent = new JackingActivityEventEditor();
      tagEvent.order = 1;
      tagEvent.guid = this.getGuid();
      tagEvent.description = 'Tagging';
      tagEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(tagEvent);

      const pinEvent = new JackingActivityEventEditor();
      pinEvent.order = 2;
      pinEvent.guid = this.getGuid();
      pinEvent.description = 'Pinned';
      pinEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(pinEvent);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.order = 6;
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);
    } else if (activity.projectInfo.vesselClassId === 3 || activity.projectInfo.vesselClassId === 4) {
      // NG14000X
      const rpdEvent = new JackingActivityEventEditor();
      rpdEvent.order = 0;
      rpdEvent.guid = this.getGuid();
      rpdEvent.description = 'R.P.D';
      rpdEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.RPD;
      activity.events.push(rpdEvent);

      const startEvent = new JackingActivityEventEditor();
      startEvent.order = 1;
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(startEvent);

      const tagEvent = new JackingActivityEventEditor();
      tagEvent.order = 2;
      tagEvent.guid = this.getGuid();
      tagEvent.description = 'Tag';
      tagEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(tagEvent);

      const pinEvent = new JackingActivityEventEditor();
      pinEvent.order = 3;
      pinEvent.guid = this.getGuid();
      pinEvent.description = 'Pin';
      pinEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(pinEvent);

      const mainCraneSlewBrakeEvent = new JackingActivityEventEditor();
      mainCraneSlewBrakeEvent.order = 4;
      mainCraneSlewBrakeEvent.guid = this.getGuid();
      mainCraneSlewBrakeEvent.description =
        'Confirm that the main crane slew brakes have been lifted';
      mainCraneSlewBrakeEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.CraneSlewBreak;
      activity.events.push(mainCraneSlewBrakeEvent);

      const rpdEvent2 = new JackingActivityEventEditor();
      rpdEvent2.order = 8;
      rpdEvent2.guid = this.getGuid();
      rpdEvent2.description = 'R.P.D';
      rpdEvent2.jackingEventCollectionType =
        JackingActivityEventCollectionType.RPD;
      activity.events.push(rpdEvent2);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.order = 10;
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);
    } else {
      throw new Error('Unknown vessel class id');
    }

    return activity;
  }

  private async buildJackDownActivity(
    operationInstance: DailyLogOperationInstance
  ): Promise<JackingActivityEditor> {
    const activity = await this.buildJackingActivityGeneric(operationInstance);

    activity.type.id = JackingActivityTypes.Departure;
    activity.type.name = 'Departure';

    // Create events for each operation step instance
    // These will be suplimented by hard-coded events for each operation type
    for (
      let i = 0;
      i < operationInstance.stepCollectionInstance.stepInstances.length;
      i++
    ) {
      const step = operationInstance.stepCollectionInstance.stepInstances[i];
      const alreadyAssignedLinkedStep = operationInstance.stepCollectionInstance.stepInstances.find(
        s => s.group === step.group && (s.guid !== null || s.guid !== undefined)
      );
      let event: JackingActivityEventEditor = null;
      if (alreadyAssignedLinkedStep.step.jackingEventCollectionType) {
        if (alreadyAssignedLinkedStep.jackingEventGuid) {
          event = activity.events.find(
            e => e.guid === alreadyAssignedLinkedStep.jackingEventGuid
          );
        }
        if (!event) {
          event = new JackingActivityEventEditor();
          event.guid = this.getGuid();
          event.description = step.step.name;
          event.jackingEventCollectionType =
            step.step.jackingEventCollectionType;
          event.order = this.getJackingDepartureEventOrderForStep(
            event,
            activity.projectInfo.vesselClassId
          );
          event.isLinkedToStep = true;
          step.jackingEventGuid = event.guid;
          activity.events.push(event);
        }
      }
    }

    if (activity.projectInfo.vesselClassId === 1) {
      // NG5500X
      const startEvent = new JackingActivityEventEditor();
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start Jacking Down Position';
      startEvent.order = 0;
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(startEvent);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.order = 2;
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);

      const waterDepthEvent = new JackingActivityEventEditor();
      waterDepthEvent.guid = this.getGuid();
      waterDepthEvent.order = 3;
      waterDepthEvent.description = 'Water Depth';
      waterDepthEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterDepthSingle;
      activity.events.push(waterDepthEvent);

      const seaWaterSunctionEvent = new JackingActivityEventEditor();
      seaWaterSunctionEvent.guid = this.getGuid();
      seaWaterSunctionEvent.order = 5;
      seaWaterSunctionEvent.description = 'Sea water suction';
      seaWaterSunctionEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.SeaWaterSuction;
      activity.events.push(seaWaterSunctionEvent);

      const davitPumpsStowedEvent = new JackingActivityEventEditor();
      davitPumpsStowedEvent.guid = this.getGuid();
      davitPumpsStowedEvent.order = 6;
      davitPumpsStowedEvent.description = 'Davit pumps stowed';
      davitPumpsStowedEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.DavitPumps;
      activity.events.push(davitPumpsStowedEvent);

      const waterDepthEventES = new JackingActivityEventEditor();
      waterDepthEventES.guid = this.getGuid();
      waterDepthEventES.order = 8;
      waterDepthEventES.description = 'Water depth E/S';
      waterDepthEventES.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterDepthSingle;
      activity.events.push(waterDepthEventES);

      const finalPosEvent = new JackingActivityEventEditor();
      finalPosEvent.guid = this.getGuid();
      finalPosEvent.order = 10;
      finalPosEvent.description = 'Final position';
      finalPosEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(finalPosEvent);

      const jettingPressureEvent = new JackingActivityEventEditor();
      jettingPressureEvent.guid = this.getGuid();
      jettingPressureEvent.order = 12;
      jettingPressureEvent.description = 'Jetting Pressure (bar)';
      jettingPressureEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.JettingPressure;
      activity.events.push(jettingPressureEvent);
    } else if (activity.projectInfo.vesselClassId === 2) {
      // NG2500X
      const startEvent = new JackingActivityEventEditor();
      startEvent.guid = this.getGuid();
      startEvent.order = 0;
      startEvent.description = 'Start Position';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(startEvent);

      const loadsAt005mPerMinEvent = new JackingActivityEventEditor();
      loadsAt005mPerMinEvent.guid = this.getGuid();
      loadsAt005mPerMinEvent.order = 1;
      loadsAt005mPerMinEvent.description = 'Loads at 0.05m/min';
      loadsAt005mPerMinEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadOnly;
      activity.events.push(loadsAt005mPerMinEvent);

      const loadsAtJackingSpeedEvent = new JackingActivityEventEditor();
      loadsAtJackingSpeedEvent.guid = this.getGuid();
      loadsAtJackingSpeedEvent.order = 2;
      loadsAtJackingSpeedEvent.description = 'Loads at jacking speed';
      loadsAtJackingSpeedEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadOnly;
      activity.events.push(loadsAtJackingSpeedEvent);

      const waterDepthEvent = new JackingActivityEventEditor();
      waterDepthEvent.guid = this.getGuid();
      waterDepthEvent.order = 5;
      waterDepthEvent.description = 'Water Depth';
      waterDepthEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterDepthPerLeg;
      activity.events.push(waterDepthEvent);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.order = 6;
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);

      const waterDepthEventES = new JackingActivityEventEditor();
      waterDepthEventES.guid = this.getGuid();
      waterDepthEventES.order = 8;
      waterDepthEventES.description = 'Water Depth E/S';
      waterDepthEventES.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterDepthPerLeg;
      activity.events.push(waterDepthEventES);

      const penetrationsEvent2 = new JackingActivityEventEditor();
      penetrationsEvent2.guid = this.getGuid();
      penetrationsEvent2.order = 9;
      penetrationsEvent2.description = 'Penetrations';
      penetrationsEvent2.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent2);

      const finalPosEvent = new JackingActivityEventEditor();
      finalPosEvent.guid = this.getGuid();
      finalPosEvent.order = 12;
      finalPosEvent.description = 'Final position';
      finalPosEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(finalPosEvent);
    } else if (activity.projectInfo.vesselClassId === 3 || activity.projectInfo.vesselClassId === 4) {
      // NG14000X
      const rpdEvent = new JackingActivityEventEditor();
      rpdEvent.guid = this.getGuid();
      rpdEvent.order = 0;
      rpdEvent.description = 'R.P.D';
      rpdEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.RPD;
      activity.events.push(rpdEvent);

      const startEvent = new JackingActivityEventEditor();
      startEvent.guid = this.getGuid();
      startEvent.order = 1;
      startEvent.description = 'Load/Comm Lower';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(startEvent);

      const waterDepthEvent = new JackingActivityEventEditor();
      waterDepthEvent.guid = this.getGuid();
      waterDepthEvent.order = 3;
      waterDepthEvent.description = 'Water Depth';
      waterDepthEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterDepthPerLeg;
      activity.events.push(waterDepthEvent);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.order = 4;
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);

      const waterTightIntegrityEvent = new JackingActivityEventEditor();
      waterTightIntegrityEvent.guid = this.getGuid();
      waterTightIntegrityEvent.order = 5;
      waterTightIntegrityEvent.description = 'Watertight Integrity';
      waterTightIntegrityEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterTightIntegrity;
      activity.events.push(waterTightIntegrityEvent);

      const wtIntegrityExtended = new JackingActivityEventEditor();
      wtIntegrityExtended.guid = this.getGuid();
      wtIntegrityExtended.order = 6;
      wtIntegrityExtended.description = 'WT Integrity';
      wtIntegrityExtended.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterTightIntegrityExtended;
      activity.events.push(wtIntegrityExtended);

      const waterDepthEventES = new JackingActivityEventEditor();
      waterDepthEventES.guid = this.getGuid();
      waterDepthEventES.order = 8;
      waterDepthEventES.description = 'Water Depth E/S';
      waterDepthEventES.jackingEventCollectionType =
        JackingActivityEventCollectionType.WaterDepthPerLeg;
      activity.events.push(waterDepthEventES);

      const penetrationsEvent2 = new JackingActivityEventEditor();
      penetrationsEvent2.guid = this.getGuid();
      penetrationsEvent2.order = 9;
      penetrationsEvent2.description = 'Penetrations';
      penetrationsEvent2.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent2);

      const clear5mEvent = new JackingActivityEventEditor();
      clear5mEvent.guid = this.getGuid();
      clear5mEvent.order = 11;
      clear5mEvent.description = '5m Clear';
      clear5mEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(clear5mEvent);

      const finalPosEvent = new JackingActivityEventEditor();
      finalPosEvent.guid = this.getGuid();
      finalPosEvent.order = 12;
      finalPosEvent.description = 'Final position';
      finalPosEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(finalPosEvent);
    } else {
      throw new Error('Unknown vessel class id');
    }

    return activity;
  }

  // replaced by the combined jacking arrival + jack up, but kept for old ops
  private async buildObsoleteJackUpActivity(
    operationInstance: DailyLogOperationInstance
  ): Promise<JackingActivityEditor> {
    // For Jack Up the activity should already exist
    const activities = (await this.dbContext.getAll(
      this.jackingActivitiesTable
    )) as JackingActivityEditor[];

    // although the activity.startDate.dateTime is no in correct UTC (we need to add system offset for that, as we just took it from local storage)
    // it doesn't matter as operationInstance isn't correct either, as offset was added earlier in save process
    for (const activity of activities) {
      // set start date only as that's all we need to use below
      activity.startDate.dateTime = await this.dateParserService.deserializeDate(activity.startDate.dateTime, true, this.timeZoneService);
    }

    let filteredActivities = activities.filter(a => a.type.tag === 'arrival'
      && a.startDate.dateTime <= operationInstance.startTime.dateTime
      && (a.cancellationTime === null || a.cancellationTime.dateTime === null)
      && (a.secondaryOperationId === null && a.localSecondaryOperationId === null)
    );
    const sortDescending = function (a, b) {
      if (a > b) {
        return -1;
      }
      if (a < b) {
        return +1;
      }
      return 0;
    };

    filteredActivities = filteredActivities.sort(function (a, b) {
      return sortDescending(a.startDate.dateTime.toMillis(), b.startDate.dateTime.toMillis());
    });

    const chosenActivity = filteredActivities[0];

    if (chosenActivity) {
      const vessel = await this.vesselService.getEditor();
      await this.deserializeJackingActivityDates(chosenActivity, false, true); // work around as PtLuxonDate objects need to be reassigned
      chosenActivity.localSecondaryOperationId = operationInstance.localId;
      chosenActivity.secondaryOperationId = operationInstance.id;

      // Create events for each operation step instance
      // These will be suplimented by hard-coded events for each operation type
      for (
        let i = 0;
        i < operationInstance.stepCollectionInstance.stepInstances.length;
        i++
      ) {
        const step = operationInstance.stepCollectionInstance.stepInstances[i];
        const alreadyAssignedLinkedStep = operationInstance.stepCollectionInstance.stepInstances.find(
          s =>
            s.group === step.group && (s.guid !== null || s.guid !== undefined)
        );
        let event: JackingActivityEventEditor = null;
        if (alreadyAssignedLinkedStep.step.jackingEventCollectionType) {
          if (alreadyAssignedLinkedStep.jackingEventGuid) {
            event = chosenActivity.events.find(
              e => e.guid === alreadyAssignedLinkedStep.jackingEventGuid
            );
          }
          if (!event) {
            event = new JackingActivityEventEditor();
            event.guid = this.getGuid();
            event.description = step.step.name;
            event.jackingEventCollectionType =
              step.step.jackingEventCollectionType;
            event.order = this.getJackingArrivalEventOrderForStep(
              event,
              vessel.vesselClassId
            );
            event.isLinkedToStep = true;
            step.jackingEventGuid = event.guid;
            chosenActivity.events.push(event);
          }
        }
      }
    }

    return chosenActivity;
  }

  private getGuid(): string {
    const chars = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'A',
      'B',
      'C',
      'D',
      'E',
      'F'
    ];
    let str = '';
    for (let i = 0; i < 36; i++) {
      str =
        str +
        (i === 8 || i === 13 || i === 18 || i === 23
          ? '-'
          : chars[Math.floor(Math.random() * chars.length)]);
    }
    return str;
  }

  // Far from the best solution but the system was orginally designed to
  // sort events in chronological order and not in a fixed order.
  // Only gets order for events that are linked to steps
  private getJackingArrivalEventOrderForStep(
    evnt: JackingActivityEventEditor,
    currentVesselClassId: number
  ): number {
    if (currentVesselClassId === 1) {
      // NG5500X
      if (evnt.description === 'Pinned') {
        return 2;
      }
      if (evnt.description === 'Initial Penetrations') {
        return 3;
      }
      if (evnt.description === 'Commence active preload') {
        return 4;
      }
      if (evnt.description === 'Complete active preload') {
        return 5;
      }
      if (evnt.description === 'Zero AG') {
        return 7;
      }
      if (evnt.description === 'Commence passive preload') {
        return 8;
      }
      if (evnt.description === 'Timed holds as per COA Start') {
        return 10;
      }
      if (evnt.description === 'Comp holds as per COA End') {
        return 11;
      }
    } else if (currentVesselClassId === 2) {
      // NG2500X
      if (evnt.description === 'Pinned') {
        return 2;
      }
      if (evnt.description === 'Comm. in water Predrive') {
        return 3;
      }
      if (evnt.description === 'Initial Penetration') {
        return 4;
      }
      if (evnt.description === 'Zero AG') {
        return 5;
      }
      if (evnt.description === 'Comm. out water preload') {
        return 7;
      }
      if (evnt.description === 'Comp. out water preload') {
        return 8;
      }
      if (evnt.description === 'Comm. 30 minute holds') {
        return 9;
      }
      if (evnt.description === 'Comp. 30 minute holds') {
        return 10;
      }
    } else if (currentVesselClassId === 3 || currentVesselClassId === 4) {
      // NG14000X
      if (evnt.description === 'Pin') {
        return 3;
      }
      // NG14000X
      if (evnt.description === 'Tagging' || evnt.description === 'Tag') {
        return 2;
      }
      if (evnt.description === 'Comm. Predrive') {
        return 5;
      }
      if (evnt.description === 'Initial Penetration') {
        return 6;
      }
      if (evnt.description === 'Comp. Predrive') {
        return 7;
      }
      if (evnt.description === 'Zero AG') {
        return 9;
      }
      if (evnt.description === 'Comm. Preload') {
        return 11;
      }
      if (evnt.description === 'Comp. Preload') {
        return 12;
      }
      if (evnt.description === 'Comm. X min holds') {
        return 13;
      }
      if (evnt.description === 'Comp. X min holds') {
        return 14;
      }
    }
  }

  private getJackingDepartureEventOrderForStep(
    evnt: JackingActivityEventEditor,
    currentVesselClassId: number
  ): number {
    if (currentVesselClassId === 1) {
      // NG5500X
      if (evnt.description === 'Zero Air Gap') {
        return 1;
      }
      if (evnt.description === 'Watertight Integrity Checks') {
        return 4;
      }
      if (evnt.description === 'At Floating Draft') {
        return 9;
      }
      if (evnt.description === 'Commence pulling legs') {
        return 10;
      }
    } else if (currentVesselClassId === 2) {
      // NG2500X
      if (evnt.description === 'Zero Air Gap') {
        return 3;
      }
      if (evnt.description === 'Watertight Integrity Checks') {
        return 4;
      }
      if (evnt.description === 'At Floating Draft') {
        return 10;
      }
      if (evnt.description === 'Commence pulling legs') {
        return 11;
      }
    } else if (currentVesselClassId === 3 || currentVesselClassId === 4) {
      // NG14000X
      if (evnt.description === 'Zero AG') {
        return 2;
      }
      if (evnt.description === 'Floating Draft') {
        return 7;
      }
      if (evnt.description === 'Comm. Pulling') {
        return 10;
      }
      if (evnt.description === '5m Clear') {
        return 11;
      }
    }
  }

  private createHardCodedJackingEventsForJackingArrival(activity: JackingActivityEditor) {
    // Add hardcoded events per vessel class
    if (activity.projectInfo.vesselClassId === 1) {
      // NG5500X -
      const startEvent = new JackingActivityEventEditor();
      startEvent.order = 0;
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start Position';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(startEvent);

      const penetrationsAfterActivePreloadEvent = new JackingActivityEventEditor();
      penetrationsAfterActivePreloadEvent.order = 6;
      penetrationsAfterActivePreloadEvent.guid = this.getGuid();
      penetrationsAfterActivePreloadEvent.description =
        'Penetrations after active preload';
      penetrationsAfterActivePreloadEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsAfterActivePreloadEvent);

      const airGapEvent = new JackingActivityEventEditor();
      airGapEvent.guid = this.getGuid();
      airGapEvent.order = 9;
      airGapEvent.description = 'Air gap';
      airGapEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.AirGap;
      activity.events.push(airGapEvent);
    } else if (activity.projectInfo.vesselClassId === 2) {
      // NG2500X - Zaratan
      const startEvent = new JackingActivityEventEditor();
      startEvent.order = 0;
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start Position';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.ExtOnly;
      activity.events.push(startEvent);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.order = 6;
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);
    } else if (activity.projectInfo.vesselClassId === 3 || activity.projectInfo.vesselClassId === 4) {
      // NG14000X - Hydra, Kraken, Leviathan
      const rpdEvent = new JackingActivityEventEditor();
      rpdEvent.order = 0;
      rpdEvent.guid = this.getGuid();
      rpdEvent.description = 'R.P.D';
      rpdEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.RPD;
      activity.events.push(rpdEvent);

      const startEvent = new JackingActivityEventEditor();
      startEvent.order = 1;
      startEvent.guid = this.getGuid();
      startEvent.description = 'Start';
      startEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.LoadAndExt;
      activity.events.push(startEvent);

      const mainCraneSlewBrakeEvent = new JackingActivityEventEditor();
      mainCraneSlewBrakeEvent.order = 4;
      mainCraneSlewBrakeEvent.guid = this.getGuid();
      mainCraneSlewBrakeEvent.description =
        'Confirm that the main crane slew brakes have been lifted';
      mainCraneSlewBrakeEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.CraneSlewBreak;
      activity.events.push(mainCraneSlewBrakeEvent);

      const rpdEvent2 = new JackingActivityEventEditor();
      rpdEvent2.order = 8;
      rpdEvent2.guid = this.getGuid();
      rpdEvent2.description = 'R.P.D';
      rpdEvent2.jackingEventCollectionType =
        JackingActivityEventCollectionType.RPD;
      activity.events.push(rpdEvent2);

      const penetrationsEvent = new JackingActivityEventEditor();
      penetrationsEvent.order = 10;
      penetrationsEvent.guid = this.getGuid();
      penetrationsEvent.description = 'Penetrations';
      penetrationsEvent.jackingEventCollectionType =
        JackingActivityEventCollectionType.Penetrations;
      activity.events.push(penetrationsEvent);
    } else {
      throw new Error('Unknown vessel class id');
    }
  }

  // will only create events for steps that have a jacking event collection type
  private createOperationStepEventsForJackingArrival(activity: JackingActivityEditor, operationInstance) {
    // Create events for each operation step instance
    // These will be suplimented by hard-coded events for each operation type
    for (let i = 0; i < operationInstance.stepCollectionInstance.stepInstances.length; i++) {
      const step = operationInstance.stepCollectionInstance.stepInstances[i];
      const alreadyAssignedLinkedStep = operationInstance.stepCollectionInstance.stepInstances.find(
        s => s.group === step.group && (s.guid !== null || s.guid !== undefined)
      );
      let event: JackingActivityEventEditor = null;

      if (alreadyAssignedLinkedStep.step.jackingEventCollectionType) {
        if (alreadyAssignedLinkedStep.jackingEventGuid) {
          event = activity.events.find(
            e => e.guid === alreadyAssignedLinkedStep.jackingEventGuid
          );
        }
        if (!event) {
          event = new JackingActivityEventEditor();
          event.guid = this.getGuid();
          event.description = step.step.name;
          event.jackingEventCollectionType =
            step.step.jackingEventCollectionType;
          event.order = this.getJackingArrivalEventOrderForStep(
            event,
            activity.projectInfo.vesselClassId
          );
          event.isLinkedToStep = true;
          step.jackingEventGuid = event.guid;
          activity.events.push(event);
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/jacking-activity/jacking-activity.service.ts